import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { CalendarDialog } from "@/components/CalendarDialog";
import { Link } from "react-router-dom";
import {
  Target,
  Users,
  Lightbulb,
  ShieldCheck,
  Wrench,
  CheckCircle,
  ArrowRight,
  Globe,
  MapPin,
  Star,
  Megaphone,
  Cpu,
  Mail,
} from "lucide-react";

const values = [
  {
    icon: Wrench,
    title: "Trades & Local Service Specialists",
    description:
      "We focus on Australian tradies and local service businesses — like cleaners, gyms, salons and pest control. Every strategy fits how location-based, job- and booking-driven businesses actually win work.",
  },
  {
    icon: Target,
    title: "Outcome-Focused",
    description:
      "We measure success by enquiries, bookings and jobs won. If it doesn't move those numbers, we don't do it.",
  },
  {
    icon: ShieldCheck,
    title: "Flexible, Results-Driven Service",
    description:
      "We earn your business every month with results — clear reporting and consistent performance that supports steady growth.",
  },
  {
    icon: Lightbulb,
    title: "Systems, Not Campaigns",
    description:
      "We don't just run ads. We build complete digital growth systems — websites, Local SEO, ads and reviews — all working together to grow your business.",
  },
];

const teamFacts = [
  "Years of experience working with Australian trades and local service businesses — electricians, plumbers, builders, roofers, HVAC, cleaners, gyms, salons, pest control and more",
  "Specialist knowledge of how location-based and booking-driven businesses operate locally across Australia",
  "Focused on measurable outcomes — visibility, enquiries, bookings and revenue, not just clicks and impressions",
  "Dedicated account management — you'll always know who to call",
];

const serviceOverview = [
  { icon: Globe, label: "SMART Websites", href: "/services/websites" },
  { icon: MapPin, label: "Google Business Profile & Local SEO", href: "/services/gbp-seo" },
  { icon: Star, label: "Reputation & Reviews", href: "/services/reputation" },
  { icon: Megaphone, label: "Lead Generation Ads", href: "/services/lead-generation" },
  { icon: Cpu, label: "Automation & AI Tools", href: "/services/automation" },
  { icon: Mail, label: "Reactivation Campaigns", href: "/services/reactivation" },
];

const aboutSchema = {
  "@context": "https://schema.org",
  "@type": "AboutPage",
  name: "About FullScale Marketing",
  description: "FullScale Marketing is Australia's specialist digital marketing agency for trades and local service businesses.",
  url: "https://fullscalemarketing.com.au/about",
  mainEntity: {
    "@type": "Organization",
    name: "FullScale Marketing",
    url: "https://fullscalemarketing.com.au",
    description: "Digital marketing agency specialising in Australian trades and local service businesses.",
  },
};

export default function About() {
  return (
    <>
      <Helmet>
        <title>About Us | Digital Marketing for Trades & Service Businesses</title>
        <meta
          name="description"
          content="FullScale Marketing is Australia's specialist digital marketing agency for tradies and local service businesses. We build growth systems that deliver visibility, reviews, and booked jobs."
        />
        <link rel="canonical" href="https://fullscalemarketing.com.au/about" />
        <meta property="og:title" content="About FullScale Marketing | Trades & Service Business Marketing Australia" />
        <meta
          property="og:description"
          content="Australia's specialist digital marketing agency for trades and local service businesses. Systems, not campaigns."
        />
        <meta property="og:url" content="https://fullscalemarketing.com.au/about" />
        <meta property="og:type" content="website" />
        <script type="application/ld+json">{JSON.stringify(aboutSchema)}</script>
      </Helmet>

      {/* Hero */}
      <section className="relative pt-28 sm:pt-32 lg:pt-36 pb-14 sm:pb-18 lg:pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-background via-[hsl(100_25%_97%)] to-[hsl(98_30%_95%/0.6)]" />
        <div className="absolute inset-0 bg-gradient-hero-glow pointer-events-none" />
        <div className="absolute inset-0 grid-pattern opacity-30" />

        <div className="container-wide relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl mx-auto text-center"
          >
            <div className="section-badge mb-4">
              <span className="text-xs">About FullScale Marketing</span>
            </div>
            <h1 className="text-[2rem] sm:text-[2.5rem] lg:text-[3.25rem] font-extrabold text-foreground leading-[1.1] mb-4 tracking-[-0.025em]">
              We Help Australian Trades & Local Service Businesses{" "}
              <span className="text-primary">Win More Work</span>
            </h1>
            <p className="text-base sm:text-lg lg:text-xl text-muted-foreground leading-[1.7] max-w-2xl mx-auto mb-6">
              FullScale Marketing is Australia's specialist digital marketing agency for trades and
              local service businesses. We build complete growth systems — not generic
              marketing campaigns.
            </p>
            <CalendarDialog
              buttonVariant="default"
              buttonSize="lg"
              buttonClassName="h-12 text-sm font-bold rounded-xl"
            />
          </motion.div>
        </div>
      </section>

      {/* Mission */}
      <section className="section-padding bg-muted relative overflow-hidden">
        <div className="absolute inset-0 grid-pattern opacity-40" />
        <div className="container-wide relative z-10">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center max-w-5xl mx-auto">
            <motion.div
              initial={{ opacity: 0, x: -24 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="heading-section text-foreground mb-4">
                Our <span className="text-gradient">Mission</span>
              </h2>
              <p className="text-muted-foreground text-base leading-[1.75] mb-4">
                Most marketing agencies treat trades and local service businesses like every other client —
                cookie-cutter websites, generic tactics, and reports full of vanity metrics
                that don't impact your bottom line.
              </p>
              <p className="text-muted-foreground text-base leading-[1.75] mb-4">
                We started FullScale Marketing because so many talented owners — electricians, plumbers,
                builders, cleaners, salon owners, gym owners, pest control operators and more — do
                excellent work but stay invisible online. Your work and service speak for themselves.
                Your digital presence should too.
              </p>
              <p className="text-muted-foreground text-base leading-[1.75] mb-4">
                Every strategy we build is grounded in how location-based, job- and booking-driven
                businesses actually win work: Google local visibility, trust signals (reviews), speed
                to response, and consistent follow-up. We don't guess. We use data, proven systems
                and deep industry knowledge to drive real results.
              </p>
              <p className="text-foreground font-semibold text-base leading-[1.75]">
                Our mission is simple:{" "}
                <span className="text-primary">
                  help quality trades and local service businesses in Australia get the visibility,
                  bookings and recognition they deserve.
                </span>
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 24 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="card-lift bg-card rounded-2xl p-6 sm:p-8 border border-border/60 shadow-card"
            >
              <h3 className="text-lg font-bold text-foreground mb-4 flex items-center gap-2">
                <Users className="w-5 h-5 text-primary" />
                Who We Are
              </h3>
              <ul className="space-y-3">
                {teamFacts.map((fact, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <CheckCircle className="w-4 h-4 text-primary mt-1 flex-shrink-0" />
                    <span className="text-muted-foreground text-[0.9375rem] leading-[1.65]">
                      {fact}
                    </span>
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Why Trades */}
      <section className="section-padding bg-background relative overflow-hidden">
        <div className="absolute inset-0 grid-pattern opacity-25" />
        <div className="container-wide relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="max-w-3xl mx-auto text-center mb-8 sm:mb-10"
          >
            <h2 className="heading-section text-foreground mb-3">
              Why We Focus on{" "}
              <span className="text-gradient">Trades & Local Service Businesses</span>
            </h2>
            <p className="text-muted-foreground text-base max-w-xl mx-auto leading-[1.7]">
              Trades and local service businesses operate differently. Location-based work, defined
              service areas, and trust-driven decisions need a marketing approach built around how
              customers actually choose a local business.
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 gap-4 sm:gap-6 max-w-4xl mx-auto">
            {values.map((value, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1, duration: 0.5 }}
                className="card-lift bg-card rounded-xl p-5 sm:p-6 border border-border/60 shadow-card"
              >
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center mb-3">
                  <value.icon className="w-5 h-5 text-primary" />
                </div>
                <h3 className="text-base sm:text-lg font-bold text-foreground mb-2">
                  {value.title}
                </h3>
                <p className="text-muted-foreground text-[0.9375rem] leading-[1.65]">
                  {value.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* What We Offer - Internal linking */}
      <section className="section-padding bg-muted relative overflow-hidden">
        <div className="absolute inset-0 grid-pattern opacity-40" />
        <div className="container-wide relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-3xl mx-auto text-center mb-8"
          >
            <h2 className="heading-section text-foreground mb-3">
              Our <span className="text-gradient">Services</span>
            </h2>
            <p className="text-muted-foreground text-base max-w-xl mx-auto leading-[1.7]">
              A complete, modular growth system — pick what you need now and scale as your business grows.
            </p>
          </motion.div>

          <div className="flex flex-col gap-2 sm:grid sm:grid-cols-3 sm:gap-3 max-w-3xl mx-auto">
            {serviceOverview.map((service, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.06, duration: 0.4 }}
              >
                <Link
                  to={service.href}
                  className="group flex items-center gap-3 bg-card rounded-lg p-4 border border-border/50 hover:border-primary/30 hover:shadow-md transition-all duration-300"
                >
                  <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0 group-hover:bg-primary/20 transition-colors">
                    <service.icon className="w-4 h-4 text-primary" />
                  </div>
                  <span className="text-foreground text-sm font-semibold group-hover:text-primary transition-colors flex-1">
                    {service.label}
                  </span>
                  <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors sm:hidden" />
                </Link>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mt-6"
          >
            <Link
              to="/services"
              className="inline-flex items-center gap-2 text-primary font-semibold text-sm hover:underline underline-offset-4"
            >
              View all services
              <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </motion.div>
        </div>
      </section>
    </>
  );
}
